import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import { PERMISSION_OPTIONS } from '../../fixtures';
import PermissionsMatrix from './PermissionsMatrix';
import { buildColumns } from './columns';

const COLUMNS = buildColumns(PERMISSION_OPTIONS);

const checkboxCell = (key, label, overrides = {}) => ({
  key,
  type: 'role',
  label,
  checked: false,
  disabled: false,
  lockReason: null,
  ...overrides,
});

const cellsFor = (name, overrides = {}) => [
  checkboxCell(
    'fleet-administrator',
    `Fleet Admin for ${name}`,
    overrides.fleet,
  ),
  checkboxCell('report-user', `Report for ${name}`, overrides.report),
  checkboxCell('management-role', `Asset Mgmt. for ${name}`, {
    type: 'group',
    ...overrides.group,
  }),
  {
    key: 'management-role:tier',
    type: 'tier',
    label: `Mgmt. Role for ${name}`,
    value: null,
    options: PERMISSION_OPTIONS[2].choices,
    disabled: false,
    lockReason: null,
    ...overrides.tier,
  },
  checkboxCell('visibleAsExternalRecipient', `External for ${name}`, {
    type: 'discoverability',
    ...overrides.external,
  }),
  checkboxCell('visibleAsInternalRecipient', `Internal for ${name}`, {
    type: 'discoverability',
    ...overrides.internal,
  }),
];

const rowFor = (id, name, overrides) => ({
  id,
  name,
  invalid: false,
  cells: cellsFor(name, overrides),
});

const GROUPS = [
  { id: 't1', name: 'Amazon', rows: [rowFor('4711', 'Account Name A')] },
  { id: 't2', name: 'Team B', rows: [rowFor('5222', 'Account Name 1')] },
];

const renderMatrix = (props = {}) =>
  render(
    <ConfigProvider>
      <PermissionsMatrix
        columns={COLUMNS}
        groups={GROUPS}
        onCellChange={() => {}}
        {...props}
      />
    </ConfigProvider>,
  );

describe('PermissionsMatrix', () => {
  describe('the two-tier header', () => {
    it('spans the bands over the columns they cover', () => {
      renderMatrix();

      expect(
        screen.getByRole('columnheader', { name: 'Permissions' }),
      ).toHaveAttribute('colspan', '4');
      expect(
        screen.getByRole('columnheader', { name: 'Discoverability' }),
      ).toHaveAttribute('colspan', '2');
    });

    // ACCOUNT shares the column headings' fill by sharing their row. Asserted
    // structurally rather than on a colour, since the fill is a token.
    it('sets ACCOUNT beside the other headings rather than spanning both rows', () => {
      renderMatrix();

      const account = screen.getByRole('columnheader', { name: 'Account' });
      const fleetAdmin = screen.getByRole('columnheader', {
        name: /Fleet Admin/,
      });

      expect(account).not.toHaveAttribute('rowspan');
      expect(account.closest('tr')).toBe(fleetAdmin.closest('tr'));
    });

    it('leaves the corner above ACCOUNT clear of a heading', () => {
      renderMatrix();

      const bandRow = screen
        .getByRole('columnheader', { name: 'Permissions' })
        .closest('tr');

      expect(bandRow.firstChild.tagName).toBe('TD');
      expect(bandRow.firstChild).toBeEmptyDOMElement();
    });

    it('names a column per role, with the group split into on/off and tier', () => {
      renderMatrix();

      ['Fleet Admin', 'Report', 'Asset Mgmt.', 'Mgmt. Role'].forEach(
        (title) => {
          expect(
            screen.getByRole('columnheader', { name: new RegExp(title) }),
          ).toBeInTheDocument();
        },
      );
    });

    it('explains the discoverability columns, whose sense is easy to invert', () => {
      renderMatrix();

      expect(
        screen.getByRole('button', { name: 'About External' }),
      ).toBeInTheDocument();
      expect(
        screen.getByRole('button', { name: 'About Internal' }),
      ).toBeInTheDocument();
    });
  });

  describe('grouping', () => {
    it('renders one band per team, in order', () => {
      renderMatrix();

      const bands = screen.getAllByRole('columnheader', {
        name: /Amazon|Team B/,
      });

      expect(bands.map((band) => band.textContent)).toEqual([
        'Amazon',
        'Team B',
      ]);
    });

    it('closes each team in its own row group', () => {
      const { container } = renderMatrix();
      const bodies = Array.from(container.querySelectorAll('tbody'));

      expect(bodies).toHaveLength(2);
      expect(
        bodies.map(
          (body) => body.querySelector('th[scope="rowgroup"]').textContent,
        ),
      ).toEqual(['Amazon', 'Team B']);

      expect(
        bodies.map((body) => body.querySelectorAll('th[scope="row"]').length),
      ).toEqual([1, 1]);
    });

    it('renders the single collapsed row in Apply to All', () => {
      renderMatrix({
        groups: [
          {
            id: 'all',
            name: 'All Teams',
            rows: [rowFor('all', 'All Accounts')],
          },
        ],
      });

      expect(
        screen.getByRole('columnheader', { name: 'All Teams' }),
      ).toBeInTheDocument();
      expect(
        screen.getByRole('rowheader', { name: 'All Accounts' }),
      ).toBeInTheDocument();
    });
  });

  describe('cells', () => {
    it('reports the row, the column and the new value, and decides nothing', () => {
      const onCellChange = jest.fn();
      renderMatrix({ onCellChange });

      fireEvent.click(
        screen.getByRole('checkbox', { name: 'Report for Account Name A' }),
      );

      expect(onCellChange).toHaveBeenCalledWith('4711', 'report-user', true);
    });

    it('shows an em dash and no control when no tier is held', () => {
      renderMatrix();

      expect(
        screen.getByLabelText('Mgmt. Role for Account Name A — none'),
      ).toHaveTextContent('—');
      expect(
        screen.queryByRole('button', { name: 'Mgmt. Role for Account Name A' }),
      ).not.toBeInTheDocument();
    });

    it('draws the tier as a single actionable tag', () => {
      renderMatrix({
        groups: [
          {
            id: 't1',
            name: 'Amazon',
            rows: [
              rowFor('4711', 'Account Name A', {
                tier: { value: { label: 'Member', roleId: 'role-member' } },
              }),
            ],
          },
        ],
      });

      const trigger = screen.getByRole('button', {
        name: 'Mgmt. Role for Account Name A',
      });

      expect(trigger).toContainElement(screen.getByText('Member'));
      expect(trigger).toHaveAttribute('aria-haspopup', 'listbox');
      expect(trigger).toHaveAttribute('aria-expanded', 'false');
    });

    it('offers the other tiers behind the one held', () => {
      renderMatrix({
        groups: [
          {
            id: 't1',
            name: 'Amazon',
            rows: [
              rowFor('4711', 'Account Name A', {
                tier: { value: { label: 'Member', roleId: 'role-member' } },
              }),
            ],
          },
        ],
      });

      fireEvent.click(
        screen.getByRole('button', { name: 'Mgmt. Role for Account Name A' }),
      );

      expect(
        screen.getByRole('option', { name: 'Manager' }),
      ).toBeInTheDocument();
      expect(screen.getByRole('option', { name: 'Member' })).toHaveAttribute(
        'aria-selected',
        'true',
      );
    });

    it('portals the tier panel outside the table scroll container', () => {
      const { container } = renderMatrix({
        groups: [
          {
            id: 't1',
            name: 'Amazon',
            rows: [
              rowFor('4711', 'Account Name A', {
                tier: { value: { label: 'Member', roleId: 'role-member' } },
              }),
            ],
          },
        ],
      });

      fireEvent.click(
        screen.getByRole('button', { name: 'Mgmt. Role for Account Name A' }),
      );

      const portal = container.querySelector('#tier-options-portal');
      const scroll = container.querySelector('table').parentElement;

      expect(portal).toContainElement(screen.getByRole('listbox'));
      expect(scroll).not.toContainElement(portal);

      const stacked = Array.from(portal.querySelectorAll('*'))
        .map((node) => window.getComputedStyle(node).zIndex)
        .filter((z) => z && z !== 'auto');

      expect(stacked).toContain('1');
    });

    it('does not paint the selected tier behind its tag', () => {
      renderMatrix({
        groups: [
          {
            id: 't1',
            name: 'Amazon',
            rows: [
              rowFor('4711', 'Account Name A', {
                tier: { value: { label: 'Member', roleId: 'role-member' } },
              }),
            ],
          },
        ],
      });

      fireEvent.click(
        screen.getByRole('button', { name: 'Mgmt. Role for Account Name A' }),
      );

      const label = screen
        .getByRole('option', { selected: true })
        .querySelector('.label');
      const { backgroundColor, color } = window.getComputedStyle(label);

      expect(backgroundColor).not.toBe('rgb(28, 110, 230)');
      expect(color).not.toBe('rgb(255, 255, 255)');
      expect(backgroundColor).toBe('rgba(110, 190, 255, 0.4)');
    });

    it('reports the chosen tier as a role id', () => {
      const onCellChange = jest.fn();
      renderMatrix({
        onCellChange,
        groups: [
          {
            id: 't1',
            name: 'Amazon',
            rows: [
              rowFor('4711', 'Account Name A', {
                tier: { value: { label: 'Member', roleId: 'role-member' } },
              }),
            ],
          },
        ],
      });

      fireEvent.click(
        screen.getByRole('button', { name: 'Mgmt. Role for Account Name A' }),
      );
      fireEvent.click(screen.getByRole('option', { name: 'Manager' }));

      expect(onCellChange).toHaveBeenCalledWith(
        '4711',
        'management-role:tier',
        'role-manager',
      );
    });
  });

  describe('server refusals', () => {
    it('renders against the row they belong to', () => {
      renderMatrix({ errors: { 4711: ['You cannot modify your own roles.'] } });

      expect(screen.getByRole('alert')).toHaveTextContent(
        'You cannot modify your own roles.',
      );
    });

    describe('on the Apply to All row', () => {
      const allRow = {
        ...rowFor('all', 'All Accounts'),
        accountIds: ['4711', '5222'],
      };
      const allGroups = [{ id: 'all', name: 'All Teams', rows: [allRow] }];

      it('gathers the refusals of every account it stands for', () => {
        renderMatrix({
          groups: allGroups,
          errors: { 4711: ['Role X is not available.'], 5222: ['Nope.'] },
        });

        expect(screen.getByRole('alert')).toHaveTextContent(
          'Role X is not available. Nope.',
        );
      });

      it('says a shared refusal once', () => {
        renderMatrix({
          groups: allGroups,
          errors: { 4711: ['Same reason.'], 5222: ['Same reason.'] },
        });

        expect(screen.getByRole('alert')).toHaveTextContent('Same reason.');
        expect(screen.getByRole('alert').textContent).toBe('Same reason.');
      });

      it('renders nothing when no account was refused', () => {
        renderMatrix({ groups: allGroups, errors: {} });

        expect(screen.queryByRole('alert')).not.toBeInTheDocument();
      });
    });
  });
});
